import { useCallback, useEffect, useRef } from 'react';

/**
 * Debounced version of useCallback. Allows specifying Lodash debounce options
 * and handles the case when component unmounts with pending fn calls.
 *
 * Adapted from:
 * https://github.com/xnimorz/use-debounce/blob/master/src/useDebouncedCallback.ts
 */
export default function useDebounced<T extends unknown[], R = unknown>(
  callback: (...args: T) => R,
  delay: number,
  // Match lodash's debounce arguments
  // https://lodash.com/docs#debounce
  options: { maxWait?: number; leading?: boolean; trailing?: boolean } = {},
): [(...args: T) => void, () => void, () => void] {
  const maxWait = options.maxWait;
  const maxWaitHandler = useRef<number | null>(null);
  /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
  const maxWaitArgs = useRef<T>([] as T);

  const leading = options.leading;
  // Force trailing to be true unless explicitly set to false as we grenerally
  // don't want to "drop" function calls entirely
  const trailing = options.trailing == null ? true : options.trailing;
  const leadingCall = useRef<boolean>(false);

  const functionTimeoutHandler = useRef<number | null>(null);
  const isComponentUnmounted = useRef(false);

  const debouncedFunction = useRef(callback);
  debouncedFunction.current = callback;

  const cancelDebouncedCallback: () => void = useCallback(() => {
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    clearTimeout(functionTimeoutHandler.current);
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    clearTimeout(maxWaitHandler.current);

    maxWaitHandler.current = null;
    /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
    maxWaitArgs.current = [] as T;

    functionTimeoutHandler.current = null;
    leadingCall.current = false;
  }, []);

  useEffect(
    () => () => {
      // we use flag, as we allow to call callPending outside the hook
      isComponentUnmounted.current = true;
    },
    [],
  );

  const debouncedCallback = useCallback(
    (...args: T) => {
      maxWaitArgs.current = args;

      /* @ts-expect-error [strict-migration] The following line is not strict; please fix! */
      clearTimeout(functionTimeoutHandler.current);

      if (leadingCall.current) {
        leadingCall.current = false;
      }

      // Leading call (if desired)
      if (functionTimeoutHandler.current == null && leading && !leadingCall.current) {
        debouncedFunction.current(...args);
        leadingCall.current = true;
      }

      // Set up timer to block subsequent calls, and to execute trailing call (if desired)
      functionTimeoutHandler.current = window.setTimeout(() => {
        let shouldCallFunction = true;

        if (leading && leadingCall.current) {
          shouldCallFunction = false;
        }

        cancelDebouncedCallback();

        if (!isComponentUnmounted.current && trailing && shouldCallFunction) {
          debouncedFunction.current(...args);
        }
      }, delay);

      // Max wait time exceeded call (if desired)
      if (maxWait != null && !maxWaitHandler.current && trailing) {
        maxWaitHandler.current = window.setTimeout(() => {
          const args = maxWaitArgs.current;
          cancelDebouncedCallback();

          if (!isComponentUnmounted.current) {
            debouncedFunction.current(...args);
          }
        }, maxWait);
      }
    },
    [maxWait, delay, cancelDebouncedCallback, leading, trailing],
  );

  // Force invocation of debounced callback immediately, and clear queue
  const callPending = useCallback(() => {
    // Call pending callback only if we have anything in our queue
    if (functionTimeoutHandler.current == null) {
      return;
    }

    debouncedFunction.current.apply(null, maxWaitArgs.current);
    cancelDebouncedCallback();
  }, [cancelDebouncedCallback]);

  // At the moment, we use 3 args array so that we save backward compatibility
  return [debouncedCallback, cancelDebouncedCallback, callPending];
}
